import { FC, useEffect, useCallback, useMemo, useState, useReducer } from 'react';
import {
	Row,
	Col,
	Popconfirm,
	Button,
	Divider,
	Typography,
	Switch,
	Space,
	Modal,
	Empty,
} from 'antd';
import SearchUrlModule from '@copilot/common/components/editors/searchUrl';
import { SearchUrlType } from '@copilot/common/utils/constant';
import {
	useCampaignMemberLinkedInfoFetch,
	useUpdateLinkedInInfoFetch,
} from '@copilot/common/hooks/requests/campaignMember';
import modalManager from '@copilot/common/utils/modalManager';
import { LinkedInManager, CampaignMemberManager } from '@copilot/data';
import { useTaskMonitor, useFetch, useFetchV2, useDebounce } from '@copilot/common/hooks/common';
import notificationManager from '@copilot/common/utils/notificationManager';
import BasicContainer from '@copilot/common/components/containers/basic';
import { useSelector } from 'react-redux';
import { LinkedinSelectors } from '@copilot/common/store/selectors/linkedin';
import { LinkedInTaskManager } from '@copilot/data/managers/tasks';
import { OrganizationMemberSelectors } from '@copilot/common/store/selectors/organizationMember';
import { isSearchUrlValid, partialUpdateReducer, throwError } from '@copilot/common/utils';
import { Config } from '@copilot/common/config';
import { addLink } from '@copilot/common/utils/common';
import { LoggedOutLIWarning } from '../../const';
import { MultipleInvite } from '@copilot/data/responses/interface';
import InstructionSteps from '@copilot/common/components/instructionSteps';
import TeamMemberDropdownWithTitle from '@copilot/common/pages/campaignDashboard/teamMemberDropDown';
import { SearchInputPlaceholder } from '@copilot/common/components/search/input';
import {
	ICampaignDashboardSearchListProps,
	ManageSearchUrlInstructionSteps,
	MANAGE_SEARCH_URL_STEPS_TITLE,
	INITIAL_MULTIPLE_INVITE, CampaignType,
} from '../const';
import { getName } from '@copilot/common/utils/campaignMember/models';
import {
	withSelectedCampaignMember,
	withCampaignMemberOptions,
	withInitialCampaignMember,
} from '../hoc';
import { usePermission, validateIsEditable } from '@copilot/common/hooks/permission';
import { PermissionName } from '@copilot/common/hooks/permission/interface';
import { useSearchListTracking } from '../tracking';
import { OrgRoleTitles } from '@copilot/common/store/models/const/enum';
import {
	SearchListTableTabKeys,
	SearchListTableWithOpenProfiles,
} from '@copilot/common/pages/campaignDashboard/searchList/searchListTableWithOpenProfiles';
import { useSearchListCount } from '@copilot/common/pages/campaignDashboard/searchList/useSearchListCount';
import Title from '@copilot/common/typography/title';
import { OpenInMailInfoIcon } from '@copilot/common/pages/campaignDashboard/searchList/openInMailInfoIcon';
import { isNil, isObject, isUndefined } from 'lodash';
import { OpenProfileSearchListOverview } from '@copilot/common/pages/campaignDashboard/searchList/openProfileSearchListOverview';
import { SearchListOverview } from '@copilot/common/pages/campaignDashboard/searchList/searchListOverview';
import {
	getInMailAllocationModalConfig,
	getInMailMissingSequenceWarningModalConfig,
	getInMailPauseSequenceWarningModalConfig,
} from './util';
import { useSearchListTables } from '@copilot/common/pages/campaignDashboard/searchList/useSearchListTable';
import { useDailyOpenInMailLimit } from '@copilot/common/pages/campaignDashboard/searchList/campaignDashboardSearchList/useDailyOpenInMailLimitHook';
import { useCampaignsByUserLazyQuery } from '@copilot/data/graphql/_generated';

/**
 * Tab container holding search list components
 * TODO: Delete https://cassia.atlassian.net/browse/COPILOT-8286
 */
const CampaignDashboardSearchList: FC<ICampaignDashboardSearchListProps> = ({
	campaignId,
	campaignMember,
	isTeamUser,
	campaignMemberOptions,
	onCampaignMemberSelect,
	isMissingInMailSequence,
	onSetupInMailSequence,
}) => {
	const [modal, contextHolder] = Modal.useModal();

	const updateCampaignSearchListTracking = useSearchListTracking(
		'Campaign Dashboard Search List',
		null
	);
	const [isSecondPopconfirmVisible, setIsSecondPopconfirmVisible] = useState<boolean>(false);

	const [searchListTableTab, setSearchListTableTab] = useState<
		ValueOf<typeof SearchListTableTabKeys>
	>(SearchListTableTabKeys.prospect);

	const activeMember = useSelector(OrganizationMemberSelectors.getActiveMember);
	const isAdmin = !!useSelector(OrganizationMemberSelectors.getAdminMember);
	const disabledWarning = useMemo(
		() => (isAdmin ? LoggedOutLIWarning : addLink(LoggedOutLIWarning, '/settings/linkedin')),
		[isAdmin]
	);

	const omid = useMemo(
		() => (isAdmin && campaignMember?.orgMemberId ? campaignMember.orgMemberId : undefined),
		[isAdmin, campaignMember?.orgMemberId]
	);

	const [{ counts }, refetchCount] = useSearchListCount(campaignId, campaignMember?.id, omid);
	const [
		setSearchTerm,
		,
		handleTableChange,
		pagination,
		fetchSearchListResults,
		searchListResults,
	] = useSearchListTables(campaignId, campaignMember, omid, searchListTableTab);

	const [multipleInvite, setMultipleInvite] = useState<MultipleInvite>(INITIAL_MULTIPLE_INVITE);
	const campaignMemberName = useMemo(
		() => (campaignMember ? getName(campaignMember) : undefined),
		[campaignMember]
	);

	const [searchListPermission] = usePermission(PermissionName.SearchList);
	const [ownSearchListPermission] = usePermission(PermissionName.OwnSearchList);
	const isAdvancedTeamUser =
		isTeamUser && activeMember?.orgRoles.includes(OrgRoleTitles.Advanced);
	const canEditSearchList = useMemo(
		() => validateIsEditable(searchListPermission | ownSearchListPermission),
		[searchListPermission, ownSearchListPermission]
	);

	const [, fetchLinkedInInfo] = useCampaignMemberLinkedInfoFetch();
	const [updateLinkedInFetch, updateLinkedInInfo] = useUpdateLinkedInInfoFetch();
	const [searchTasksFetch, fetchSearchTasks] = useFetch(LinkedInTaskManager.getRunningSearchTask);
	useEffect(() => {
		// Grab running search tasks for the member
		// We don't grab the one specific to the campaign because tasksystem only allows one running search task per org member at a time
		if (campaignMember?.orgMemberId) fetchSearchTasks(campaignMember.orgMemberId);
	}, [campaignMember?.orgMemberId]);

	const linkedInMeta = useSelector((state) =>
		LinkedinSelectors.getOrgMemberLinkedInMeta(state, campaignMember?.orgMemberId)
	);
	const [campaignInitializer, initializeCampaign] = useFetch(
		LinkedInManager.initializeLinkedInCampaign
	);
	const [, rejectConnection] = useFetch(LinkedInManager.rejectConnection);

	const taskId = useMemo(
		() =>
			// Use either the started task or the existing task. It's possible for this to be undefined
			campaignInitializer.data ?? searchTasksFetch.data?.taskId,
		[campaignInitializer.data, searchTasksFetch.data?.taskId]
	);
	// Use task monitor will allow for undefined taskId right now because there isn't a way to conditionally call hooks without breaking Rule of Hooks
	const [searchTask] = useTaskMonitor(taskId, undefined, undefined, searchTasksFetch.data);
	const searchProgress = !searchTask || searchTask.isComplete ? undefined : 50;
	useEffect(() => {
		if (searchTask?.isComplete) fetchSearchListResults();
	}, [!!searchTask?.isComplete]);

	useEffect(() => {
		if (campaignMember?.id) {
			fetchLinkedInInfo(campaignId, campaignMember.id);
			setMultipleInvite(INITIAL_MULTIPLE_INVITE);
		}
	}, [fetchLinkedInInfo, campaignId, campaignMember?.id]);

	/**
	 * Callback for opening the modal for search building help
	 */
	const handleSearchVideoLinkClick = useCallback(() => {
		modalManager.openVideoModal({
			videoType: 'SearchVideo',
			width: 800,
			centered: true,
			hideFooter: true,
		});
	}, []);

	/**
	 * Callback for saving a search url and then performing a search
	 * @param url The url we want to update to
	 * @param initializeSearchList Whether we want to remove the old prospects or not
	 */
	const handleSaveAndSearch = async (url: string, initializeSearchList: boolean) => {
		if (!campaignId || !campaignMember?.id)
			throw new Error('Campaign or campaign id not found.');

		const isSearchUrlIdentical =
			isTeamUser || isAdmin ? false : url === campaignMember.searchUrl;
		// Prevent individual users from running the same search multiple times
		if (isSearchUrlIdentical) {
			return notificationManager.showWarnNotification({
				message: 'Search Error',
				description:
					'Your new search is identical to the old search. Search has not been performed.',
				duration: 5,
			});
		}
		if (!isAdmin && isTeamUser) {
			updateCampaignSearchListTracking({
				buttonClicked: 'Search List Tab Search - All Team Users',
			});
			if (isAdvancedTeamUser) {
				updateCampaignSearchListTracking({
					buttonClicked: 'Search List Tab Search - Advanced Team User',
				});
			}
		}
		await updateLinkedInInfo(campaignId, campaignMember.id, { searchUrl: url, multipleInvite });
		try {
			await initializeCampaign(campaignId, campaignMember.id, initializeSearchList);
			if (initializeSearchList) fetchSearchListResults();
			notificationManager.showSuccessNotification({
				message: 'Search Saved',
				description: 'Your search url has been saved and a search has been started.',
			});
		} catch (err: any) {
			const isConflict = typeof err === 'object' && err != null && err.status === 409;
			notificationManager.showErrorNotification({
				message: 'Search Error',
				description: isConflict
					? 'You are already running a search in one of your campaigns. Please wait until your search finishes.'
					: 'Unable to perform search.',
				duration: 8,
			});
		}
		return undefined;
	};

	const onSecondPopconfirmVisibleChange = useCallback((visible: boolean) => {
		if (!visible) {
			setIsSecondPopconfirmVisible(false);
		}
	}, []);

	const isInitialSearch = useMemo(
		() => !campaignMember?.lastSearchUrlUpdate && !campaignMember?.searchUrl,
		[campaignMember?.lastSearchUrlUpdate, campaignMember?.searchUrl]
	);

	const [isUpdatingDailyInMailLimit, setIsUpdatingDailyInMailLimit] = useState<boolean>(false);
	const [hasUpdatingDailyInMailLimitFailed, setHasUpdatingDailyInMailLimitFailed] =
		useState<boolean>(false);

	const { data: openInMailLimit, loading: isOpenInMailLimitUpdating } = useDailyOpenInMailLimit(
		activeMember?.organizationId,
		campaignMember?.orgMemberId
	);

	const [openInMailState, updateOpenInMailState] = useReducer(
		partialUpdateReducer<{
			isEnabled: boolean;
			dailyLimitAvailable: number;
			dailyLimit: number;
		}>, {
			dailyLimitAvailable: 0,
			dailyLimit: 0,
			isEnabled: false,
		});

	// Storing the dailyLimit and dailyLimitAvailable as a local state because the value of campaignMember?.dailyOpenInMailLimit
	// is based on the elastic search view. The value might be stale when the view is updating.
	useEffect(() => {
		const limit = campaignMember?.dailyOpenInMailLimit;
		if (isNil(limit)) return;

		const openInMailAllocatedInOtherCampaigns = (openInMailLimit?.allocated ?? 0) - limit;

		const openInMailAvailableToAllocate =
			(openInMailLimit?.limit ?? 0) - openInMailAllocatedInOtherCampaigns;
		updateOpenInMailState({
			dailyLimitAvailable: openInMailAvailableToAllocate,
			dailyLimit: limit,
		});
	}, [campaignMember?.id, openInMailLimit?.allocated, openInMailLimit?.limit]);

	const [, updateLinkedInInformation] = useFetchV2(
		CampaignMemberManager.updateCampaignMemberLinkedInInformation
	);
	async function updateDailyOpenInMailLimit(limit: number): Promise<void> {
		const campaignMemberId = campaignMember?.campaignMemberId;
		if (isUndefined(campaignMemberId))
			throwError('Unable to update due to missing campaign member id');
		if (limit > openInMailState.dailyLimitAvailable)
			throwError(`Unable to exceed ${openInMailState.dailyLimitAvailable} per day`);
		try {
			setHasUpdatingDailyInMailLimitFailed(false);
			setIsUpdatingDailyInMailLimit(true);
			await updateLinkedInInformation(campaignId, campaignMemberId, {
				dailyOpenInMailLimit: limit,
				openInMailEnabled: limit == 0 ? false : undefined,
			});
			if (limit == 0) {
				updateOpenInMailState({ isEnabled: false });
			}
		} catch {
			setHasUpdatingDailyInMailLimitFailed(true);
		} finally {
			setIsUpdatingDailyInMailLimit(false);
		}
	}

	const updateDailyOpenInMailLimitDebounce = useDebounce(updateDailyOpenInMailLimit, 1000);
	const numContactCollected =  isNil(counts.openProfile) || isNil(counts.prospect) 
		? undefined 
		: counts.openProfile + counts.prospect;

	/**
	 * Button and confirmation popup for performing a search
	 * @param searchUrl the search url we will search for
	 */
	const saveAndSearchButton = useCallback(
		(searchUrl: string) => {
			const numPeople = numContactCollected ?? 0;
			const isSearchDisabled = !linkedInMeta?.isLoggedIn || !isSearchUrlValid(searchUrl);
			const isSaving = campaignInitializer.isFetching || updateLinkedInFetch.isFetching;
			const isInitialSearchAndEmpty = !numPeople && isInitialSearch;
			if (!isInitialSearchAndEmpty && numPeople > 0) {
				return (
					<Popconfirm
						title={`Are you sure? The ${numPeople} prospects will be removed from your Prospects List.`}
						open={isSecondPopconfirmVisible}
						onOpenChange={onSecondPopconfirmVisibleChange}
						onConfirm={() => {
							handleSaveAndSearch(searchUrl, true);
						}}
						okText="Yes"
						cancelText="Cancel"
					>
						<Popconfirm
							title={`Would you still like to invite the ${numPeople} prospects from your previous search list?`}
							onConfirm={() => {
								handleSaveAndSearch(searchUrl, false);
							}}
							onCancel={() => setIsSecondPopconfirmVisible(true)}
							okText="Yes"
							cancelText="No"
							disabled={isSearchDisabled}
						>
							<Button type="primary" disabled={isSearchDisabled} loading={isSaving}>
								{isSaving ? 'Saving' : 'Run search'}
							</Button>
						</Popconfirm>
					</Popconfirm>
				);
			} else {
				return (
					<Button
						type="primary"
						disabled={isSearchDisabled}
						onClick={() => {
							handleSaveAndSearch(searchUrl, true);
						}}
						loading={isSaving}
					>
						{isSaving ? 'Saving' : 'Run search'}
					</Button>
				);
			}
		},
		[
			handleSaveAndSearch,
			onSecondPopconfirmVisibleChange,
			isSecondPopconfirmVisible,
			isInitialSearch,
			numContactCollected,
			linkedInMeta?.isLoggedIn,
			campaignInitializer.isFetching,
		]
	);

	/**
	 * Callback for removing a prospect
	 * @param connectionId The id of the connection we want to remove
	 */
	const removeProspect = useCallback(
		async (connectionId: string, campaignMemberId: string) => {
			if (campaignId) {
				try {
					await rejectConnection(campaignId, campaignMemberId, connectionId);
					await refetchCount();
					notificationManager.showSuccessNotification({
						message: 'Prospect Removed',
						description: 'Prospect removed from the sequence.',
					});
				} catch (err) {
					notificationManager.showErrorNotification({
						message: 'Prospect Removal Failed',
						description:
							'Unable to remove this prospect from the sequence. Please try again later.',
					});
				}
				// TODO: remove the fetch after the search list is saved to the store
				fetchSearchListResults();
			}
		},
		[campaignId, fetchSearchListResults]
	);

	const searchUrlTitle = useMemo(() => {
		if (isTeamUser && campaignMember?.firstName) {
			return `${campaignMember.firstName}'s search URL`;
		} else return isInitialSearch ? 'Input Sales Navigator URL' : 'Search List';
	}, [isTeamUser, campaignMember?.firstName, isInitialSearch]);
	
	const showSelectMemberInstructions = !campaignMember && campaignMemberOptions;
	useEffect(() => {
		if (isNil(campaignMember)) return;
		updateOpenInMailState({ isEnabled: campaignMember.openInMailEnabled });
	}, [campaignMember?.openInMailEnabled]);

	const [{ isFetching: isOpenInMailUpdating }, updateIsOpenInMail] = useFetch(
		CampaignMemberManager.updateCampaignMemberLinkedInInformation
	);

	const canUpdateOpenInMailSetting =
		activeMember?.isOrgAdmin || activeMember?.orgRoles.includes(OrgRoleTitles.Advanced);
	
	const [getAllocations, { loading: isLoadingAllocations }] = useCampaignsByUserLazyQuery();
	
	/**
	 * Callback to display user's InMail's allocation
	 */
	function onShowInMailAllocations(): void {
		getAllocations({ variables: { input: { orgMemberId: campaignMember?.orgMemberId } } }).then(
				({ data }) => {
					const campaigns = data?.campaigns;
					if (isNil(campaigns)) {
						notificationManager.showErrorNotification({ message: "Unable to get user's allocations. Please try again." });
						return;
					}
					const allocations: { campaignName: string, allocated: number }[] = campaigns
						.filter((campaign) => {
							return campaign.type === CampaignType.prospecting;
						})
						.map(campaign => {
							const member = campaign?.members?.find(({ orgMemberId }) => orgMemberId === campaignMember?.orgMemberId);
							return {
								campaignName: campaign.name ?? throwError('Unable to get the campaign name'),
								allocated: member?.dailyOpenInMailAllocated ?? 0
							};
						});
					modal.info(getInMailAllocationModalConfig(allocations));
				}
		).catch(() => {
			notificationManager.showErrorNotification({ message: "Unable to get user's allocations. Please try again." });
		});
	}

	return (
		<>
			{canEditSearchList && (
				<>
					<Row style={{ display: 'block' }}>
						<Col>
							<BasicContainer bordered={false}>
								<BasicContainer.Content>
									{campaignMemberOptions && (
										<TeamMemberDropdownWithTitle
											selectedMemberName={campaignMemberName}
											options={campaignMemberOptions}
											onSelect={onCampaignMemberSelect}
										/>
									)}
									{!showSelectMemberInstructions && (
										<Row>
											<Col>
												<h2>{searchUrlTitle}</h2>
											</Col>
										</Row>
									)}
									{!showSelectMemberInstructions && (
										<SearchUrlModule
											searchType={SearchUrlType.Search}
											numPeople={numContactCollected}
											updateDate={campaignMember?.lastSearchUrlUpdate}
											url={campaignMember?.searchUrl}
											isInitialSearch={isInitialSearch}
											isLoading={searchTasksFetch.isFetching}
											searchPercentage={searchProgress}
											showVideoLink={!Config.isAgency}
											saveAndSearchButton={saveAndSearchButton}
											onVideoLinkClicked={handleSearchVideoLinkClick}
											saveDisableWarning={
												!linkedInMeta?.isLoggedIn && disabledWarning
											}
											multipleInvite={isTeamUser ? multipleInvite : undefined}
											updateMultipleInvite={
												isTeamUser ? setMultipleInvite : undefined
											}
										/>
									)}
								</BasicContainer.Content>
							</BasicContainer>
						</Col>
					</Row>
					<br />
				</>
			)}
			{!showSelectMemberInstructions && (
				<Row style={{ display: 'block' }}>
					<Col>
						<BasicContainer>
							<SearchListTableWithOpenProfiles
								tableHeader={
									<Space>
										<Title level={4}>
											{isUndefined(campaignMember?.firstName)
												? null
												: `${campaignMember?.firstName}'s`}{' '}
											Search List
										</Title>
										{canUpdateOpenInMailSetting && (
											<>
												<Divider type={'vertical'} />
												<Typography.Text>
													Send InMail to Open Profiles
												</Typography.Text>
												<OpenInMailInfoIcon />
												<Switch
													onChange={(state) => {
														if (isNil(campaignMember)) return;
														if (state && isMissingInMailSequence) {
															// Open "You haven't set up an InMail messaging sequence yet" Modal
															modal.confirm(
																getInMailMissingSequenceWarningModalConfig(
																	() =>
																		onSetupInMailSequence?.()
																));
	
															return;
														} else if (state) {
															updateIsOpenInMail(
																campaignId,
																campaignMember.id,
																{ openInMailEnabled: true }
															).then(() =>
																	updateOpenInMailState({
																		isEnabled: true,
																	})
															);
														} else {
															// open "Pause InMail Sequence" Modal
															modal.confirm(
																getInMailPauseSequenceWarningModalConfig(
																	() => {
																		updateIsOpenInMail(
																			campaignId,
																			campaignMember.id,
																			{
																				openInMailEnabled: false,
																				dailyOpenInMailLimit: 0,
																			}
																			).then(() => {
																				updateOpenInMailState(
																					{
																						isEnabled: false,
																						dailyLimit: 0,
																					});
																			});
																	}
																	)
															);
														}
													}}
													loading={isOpenInMailUpdating}
													checked={openInMailState.isEnabled}
												/>
											</>
										)}
									</Space>
								} 
								tableOverview={
									searchListTableTab ===
									SearchListTableTabKeys.openProfile ? (
										<OpenProfileSearchListOverview
											count={
												isObject(pagination) ? pagination.total ?? 0 : 0
											}
											allocationLimit={openInMailLimit?.limit ?? 0}
											numAvailableToAllocate={
												openInMailState.dailyLimitAvailable
											}
											allocated={openInMailState.dailyLimit}
											onUpdateDailyLimit={(limit: number) => {
												updateOpenInMailState({ dailyLimit: limit });
												updateDailyOpenInMailLimitDebounce(limit);
											}}
											isLimitUpdating={
												isUpdatingDailyInMailLimit ||
												isOpenInMailLimitUpdating
											}
											isInMailEnabled={openInMailState.isEnabled}
											hasLimitUpdateFailed={
												hasUpdatingDailyInMailLimitFailed
											}
											canEdit={canUpdateOpenInMailSetting}
											isLoadingAllocations={isLoadingAllocations}
											onShowAllocations={onShowInMailAllocations}
										/>
									) : (
										<SearchListOverview
											count={
												isObject(pagination) ? pagination.total ?? 0 : 0
											}
										/>
									)
								}
								dataSource={searchListResults.data?.results}
								pagination={pagination}
								loading={searchListResults.isFetching}
								onStopAutomation={removeProspect}
								onChange={handleTableChange}
								onSearch={setSearchTerm}
								searchPlaceholder={SearchInputPlaceholder.Name}
								activeTabKey={searchListTableTab}
								setActiveTabKey={setSearchListTableTab}
								counts={counts ?? {}}
								locale={{
									emptyText: (
										<Empty
											image={Empty.PRESENTED_IMAGE_SIMPLE}
											description={
												searchListTableTab ===
												SearchListTableTabKeys.openProfile
													? 'No Open LinkedIn Profiles at this time for InMail outreach'
													: 'No data'
											}
										/>
									),
								}}
							/>
						</BasicContainer>
					</Col>
				</Row>
			)}
			{showSelectMemberInstructions && (
				<Row justify="center">
					<Col style={{ width: '800px', margin: '56px 0' }}>
						<InstructionSteps
							title={MANAGE_SEARCH_URL_STEPS_TITLE}
							steps={ManageSearchUrlInstructionSteps}
							stepWidth={215}
							showShadow
						/>
					</Col>
				</Row>
			)}
			{contextHolder}
		</>
	);
};

export default withSelectedCampaignMember(
	withCampaignMemberOptions(withInitialCampaignMember(CampaignDashboardSearchList))
);
